// The current business day's completed sales. Shared by both tills: the
// native POS mounts it in a Transactions tab, the legacy till opens it from
// More. Deliberately day-scoped — refunding older sales is a portal job,
// not something to do on a till mid-service.
import React, { useCallback, useEffect, useState } from 'react';
import { C } from '../../../styles/theme';
import { refundService, transactionService } from '../../../services/api';

const FONT = "'Plus Jakarta Sans', sans-serif";

// Newest sale first — and the authoritative sort, applied here rather than
// left to whichever source answered.
//
// There are three routes into this list (see getTransactionsAfterStartOfDay
// in services/api.js): the backend endpoint, the Electron local DB, and the
// browser local DB. Ordering them all the same way at the source is right
// but not sufficient — the cashier must see one order regardless of which
// answered, including offline.
//
// Sorted on (date, time), NEVER time alone. `sales_date` names a till
// session that stays open until someone closes it, so one business day
// routinely spans several calendar dates: a real session holds sales dated
// the 9th, 10th and 11th. `time` is a time-of-day string, so sorting by it
// alone ranks 22:32 on the 9th above 00:08 on the 11th — the newest sale
// buried in the middle of the list.
//
// `date`/`time` are the device's local wall clock and are what the row
// displays; `created_at` is UTC. Sorting on what is shown means the order
// on screen always matches the values on screen.
export const byNewestFirst = (rows) => [...(rows || [])].sort((a, b) => {
    const key = (r) => `${r?.date || ''} ${r?.time || ''}`;
    const left = key(a);
    const right = key(b);
    if (left === right) return 0;
    return left < right ? 1 : -1;
});

const TransactionsList = ({ salesDate, onSelect, t }) => {
    const [sales, setSales] = useState([]);
    const [refundedBySale, setRefundedBySale] = useState({});
    const [loading, setLoading] = useState(true);
    // "No transactions" is a claim about the business day, not about the
    // request. Without this the two were the same screen: a failed fetch left
    // `sales` at [] and rendered the empty state, so a cashier looking for a
    // sale they had just rung up was told it did not exist. That is how the
    // sales-date path-encoding bug survived ten days of trading unreported.
    const [failed, setFailed] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setFailed(false);
        try {
            const rows = await transactionService.getTransactionsAfterStartOfDay(salesDate);
            setSales(byNewestFirst(rows));

            // Refund totals are a nice-to-have badge, not the point of the
            // screen. If the lookup fails the cashier still needs to find
            // the sale, so this failure is swallowed rather than blanking
            // the list.
            try {
                const refunds = await refundService.forSalesDate(salesDate);
                const totals = {};
                (refunds || []).forEach((r) => {
                    totals[r.transaction_id] = (totals[r.transaction_id] || 0) + r.amount;
                });
                setRefundedBySale(totals);
            } catch {
                setRefundedBySale({});
            }
        } catch {
            setSales([]);
            setFailed(true);
        } finally {
            setLoading(false);
        }
    }, [salesDate]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <div style={{ padding: 16, fontFamily: FONT }}>{t('loading')}</div>;

    if (failed) {
        return (
            <div style={{ padding: 24, textAlign: 'center', fontFamily: FONT, color: C.muted }}>
                <div style={{ marginBottom: 12 }}>{t('transactions_load_failed')}</div>
                <button
                    type="button"
                    onClick={load}
                    style={{
                        padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
                        border: `1px solid ${C.border}`, background: C.surface,
                        color: C.ink, fontWeight: 700, fontFamily: FONT,
                    }}
                >
                    {t('retry')}
                </button>
            </div>
        );
    }

    if (sales.length === 0) {
        return (
            <div style={{ padding: 24, textAlign: 'center', fontFamily: FONT, color: C.muted }}>
                {t('no_transactions_today')}
            </div>
        );
    }

    return (
        <div style={{ fontFamily: FONT }}>
            {sales.map((sale) => {
                const refunded = refundedBySale[sale.transaction_id] || 0;
                // Compared at 2dp: float sums of currency drift, and a sale
                // refunded to the penny — in one go or in instalments — must
                // read as done.
                const isFullyRefunded =
                    Math.round((refunded - Number(sale.final_amount)) * 100) >= 0;
                // A sale with SOME money back looked identical to an
                // untouched one, so the only way to find out was to open the
                // modal. The remaining figure is shown because that is the
                // number the cashier is about to need.
                const isPartlyRefunded = !isFullyRefunded && refunded > 0;
                const stillRefundable = Number(sale.final_amount) - refunded;
                return (
                    <button
                        key={sale.transaction_id}
                        type="button"
                        onClick={() => onSelect && onSelect(sale)}
                        style={{
                            display: 'flex', width: '100%', alignItems: 'center',
                            gap: 12, padding: 12, background: C.surface,
                            border: `1px solid ${C.border}`, borderRadius: 10,
                            marginBottom: 8, cursor: 'pointer', fontFamily: FONT,
                            textAlign: 'inherit',
                        }}
                    >
                        <span style={{ flex: 1, fontWeight: 700, color: C.ink }}>
                            {sale.transaction_id}
                            <span style={{ color: C.muted, fontWeight: 400, marginInlineStart: 8 }}>
                                {sale.time}
                            </span>
                        </span>
                        {isFullyRefunded && (
                            <span style={{
                                fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                                color: C.red, background: C.redLight,
                                borderRadius: 999, padding: '2px 8px',
                            }}>
                                {t('refunded')}
                            </span>
                        )}
                        {isPartlyRefunded && (
                            <span style={{
                                fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                                color: C.amber, background: C.amberLight,
                                borderRadius: 999, padding: '2px 8px',
                                whiteSpace: 'nowrap',
                            }}>
                                {t('partly_refunded')} · {stillRefundable.toFixed(2)}
                            </span>
                        )}
                        <span style={{ color: C.muted, fontSize: 12 }}>
                            {sale.payment_method}
                        </span>
                        <span style={{ fontWeight: 800, color: C.ink }}>
                            {Number(sale.final_amount).toFixed(2)}
                        </span>
                    </button>
                );
            })}
        </div>
    );
};

export default TransactionsList;
